// React
import React, { useState } from 'react'

// Axios
import axios from 'axios'

import { getTokenFromLocalStorage } from './storage'
import { uploadImageDimension, videoThumbnailDimension, videoThumbnailTime, maximumCroppedMediaSize, maximumMediaSize, maximumVideoDuration, bottomSteps } from './variableDefaults'
import { X } from '@mui/icons-material'

// AWS Requirement — Needs to be here to prevent weird errors
window.Buffer = window.Buffer || require('buffer').Buffer

// input url string, return image file
export const urlToImageFile = async (urlString, fileName = 'image.jpg') => {
  var response = await fetch(urlString)
  var data = await response.blob()
  var metadata = {
    type: 'image/jpeg',
  }
  var file = new File([data], fileName, metadata)
  return file
}

// Input image blob, return file
export const blobImageToFile = async (blobString, fileName = 'image.jpg') => {
  
  var metadata = {
    type: 'image/jpeg',
  }
  var file = new File([blobString], fileName, metadata)
  return file
}

// to call this function: await fileToDataURL(file) — because a Promise returned, 'await' is required
// Input file, return dataURL; Accepts Images and Videos
export const fileToDataURL = async (file) => {

  return new Promise((resolve, reject) => {
    
    const reader = new FileReader()
    reader.onloadend = async () => {
      try {
        resolve(reader.result)
      } catch (err) {
        reject(err)
      }
    }
    reader.onerror = (error) => {
      reject(error)
    }
    reader.readAsDataURL(file)
  })
}

// Input Data URL, return blob
function dataURItoBlob(dataURI) {
  
  // convert base64/URLEncoded data component to raw binary data held in a string
  var byteString
  if (dataURI.split(',')[0].indexOf('base64') >= 0)
    byteString = atob(dataURI.split(',')[1])
  else
    byteString = unescape(dataURI.split(',')[1])

  // separate out the mime component
  var mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]

  // write the bytes of the string to a typed array
  var ia = new Uint8Array(byteString.length)
  for (var i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i)
  }

  return new Blob([ia], { type: mimeString })
}

// The current widths and heights of an Image, from its dataURL
export const getCurrentImageWidthsAndHeights = async (dataURL) => {
  return new Promise((resolve, reject) => {

    var img = new Image

    img.onload = function() {
      resolve({
        width: img.width,
        height: img.height,
      })
    }

    img.src = dataURL
  })
}

// The start crop widths and heights for an Image, from its dataURL
export const getStartCropImageWidthsAndHeights = async (dataURL) => {
  return new Promise((resolve, reject) => {

    var img = new Image

    img.onload = function() {
      const squareImageDimension = img.width < img.height ? img.width : img.height
      const portraitImageWidth = img.height > ((4 / 3) * img.width) ? img.width : (.75 * img.height)
      const portraitImageHeight = img.height > ((4 / 3) * img.width) ? ((4 / 3) * img.width) : img.height
      resolve({
        squareImageWidth: squareImageDimension,
        squareImageHeight: squareImageDimension,
        portraitImageWidth: portraitImageWidth,
        portraitImageHeight: portraitImageHeight,
      })
    }
    img.src = dataURL

  })
}

// The current widths and heights of a video, from its file
export const getCurrentVideoWidthsAndHeights = async (file) => {
  return new Promise((resolve, reject) => {

    const urlString = URL.createObjectURL(file)

    const videoElement = document.createElement('video')
    // videoElement.src = `${videoURLString}?${Math.round(Math.random() * 1000000)}`
    videoElement.src = urlString
    videoElement.crossOrigin = 'anonymous'
    videoElement.load()
    videoElement.addEventListener('error', (ex) => {
      reject('error when loading video file', ex)
    })

    videoElement.onloadedmetadata = () => {
      // console.log('video metadata loads')
      // console.log('videoElement ->', videoElement)
      // console.log('videoElement.videoWidth ->', videoElement.videoWidth)
      // console.log('videoElement.videoHeight ->', videoElement.videoHeight)
      resolve({
        width: videoElement.videoWidth,
        height: videoElement.videoHeight,
      })
    }
  })
}

// The start crop widths and heights for a video, from its file
export const getStartCropVideoWidthsAndHeights = async (file) => {
  return new Promise((resolve, reject) => {

    const urlString = URL.createObjectURL(file)

    const videoElement = document.createElement('video')
    // videoElement.src = `${videoURLString}?${Math.round(Math.random() * 1000000)}`
    videoElement.src = urlString
    videoElement.crossOrigin = 'anonymous'
    videoElement.load()
    videoElement.addEventListener('error', (ex) => {
      reject('error when loading video file', ex)
    })

    videoElement.onloadedmetadata = () => {
      // console.log('video metadata loads')
      // console.log('videoElement ->', videoElement)
      // console.log('videoElement.videoWidth ->', videoElement.videoWidth)
      // console.log('videoElement.videoHeight ->', videoElement.videoHeight)
      const squareVideoDimension = videoElement.videoWidth < videoElement.videoHeight ? videoElement.videoWidth : videoElement.videoHeight
      const portraitVideoWidth = videoElement.videoHeight > ((4 / 3) * videoElement.videoWidth) ? videoElement.videoWidth : (.75 * videoElement.videoHeight)
      const portraitVideoHeight = videoElement.videoHeight > ((4 / 3) * videoElement.videoWidth) ? ((4 / 3) * videoElement.videoWidth) : videoElement.videoHeight
      resolve({
        squareVideoWidth: squareVideoDimension,
        squareVideoHeight: squareVideoDimension,
        portraitVideoWidth: portraitVideoWidth,
        portraitVideoHeight: portraitVideoHeight,
      })
    }
  })
}

// Return Data URL from Image with Given Crop Dimensions
// Used for react-easy-crop image cropping
export const returnCroppedImage = async (originalDataURL, cropX, cropY, cropWidth, cropHeight) => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.src = originalDataURL

    img.onload = async function() {
      // crop image
      const canvasCrop = document.createElement('canvas')
      canvasCrop.height = cropHeight
      canvasCrop.width = cropWidth
      const contextCrop = canvasCrop.getContext('2d', { alpha: false })

      contextCrop.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight)

      // Convert the square image to a data url
      const croppedImageURL = canvasCrop.toDataURL('image/jpg', 1)

      // console.log('squareCropImageURL ->', squareCropImageURL)

      // Resize Image
      if (cropWidth > uploadImageDimension) {

        const imgResize = new Image()
        imgResize.src = croppedImageURL

        imgResize.onload = async function() {
          const canvasResize = document.createElement('canvas')
          canvasResize.width = uploadImageDimension
          canvasResize.height = Math.round(imgResize.height * (uploadImageDimension / imgResize.width))

          const contextResize = canvasResize.getContext('2d', { alpha: false })
          contextResize.drawImage(imgResize, 0, 0, canvasResize.width, canvasResize.height)

          // Convert the resized image to a data url
          const resizedImageURL = canvasResize.toDataURL('image/jpg', 1)

          // Return the resized Image
          resolve(resizedImageURL)
        }
      } else {
        resolve(croppedImageURL)
      }
    }
  })
}

// Center and crop an image into a 512x512 jpg square; 
// Return the image
export const returnSquareImage = async (file) => {

  return new Promise((resolve, reject) => {
    // Give the selected image a blob URL
    const urlString = URL.createObjectURL(file)
    // console.log('urlString ->', urlString)
    const img = new Image()
    img.src = urlString


    img.onload = async function() {
      const canvasResize = document.createElement('canvas')

      const imageResizeDimension = uploadImageDimension
      const aspectRatio = img.width / img.height

      if (aspectRatio < 1) {
        canvasResize.width = imageResizeDimension
        canvasResize.height = ~~(img.height * (imageResizeDimension / img.width))
      } else if (aspectRatio > 1) {
        canvasResize.width = ~~(img.width * (imageResizeDimension / img.height))
        canvasResize.height = imageResizeDimension
      } else {
        canvasResize.width = imageResizeDimension
        canvasResize.height = imageResizeDimension
      }
      const context = canvasResize.getContext('2d', { alpha: false })
      context.drawImage(img, 0, 0, canvasResize.width, canvasResize.height)

      // Convert the square image to a data url
      const resizedImageURL = canvasResize.toDataURL('image/jpg', 1)

      // Convert the data url to an image that can be cropped
      const resizedImageObjectURL = dataURItoBlob(resizedImageURL)
      const urlCropString = URL.createObjectURL(resizedImageObjectURL)
      const imgCrop = new Image()
      imgCrop.src = urlCropString

      // Crop the image onload
      imgCrop.onload = async function() {
    
        // crop image
        if (aspectRatio !== 1) {
          const canvasCrop = document.createElement('canvas')
          canvasCrop.height = imageResizeDimension
          canvasCrop.width = imageResizeDimension
          const contextCrop = canvasCrop.getContext('2d', { alpha: false })
    
          let startX
          let startY
          if (aspectRatio < 1) {
            startX = 0
            startY = (imgCrop.height - imageResizeDimension) / 2
          } else if (aspectRatio > 1) {
            startX = (imgCrop.width - imageResizeDimension) / 2
            startY = 0
          }
      
          contextCrop.drawImage(imgCrop, startX, startY, imageResizeDimension, imageResizeDimension, 0, 0, imageResizeDimension, imageResizeDimension)
    
          // Convert the square image to a data url
          const squareCropImageURL = canvasCrop.toDataURL('image/jpg', 1)

          // console.log('squareCropImageURL ->', squareCropImageURL)
          resolve(squareCropImageURL)
        } else {

          // console.log('resizedImageURL ->', resizedImageURL)
          resolve(resizedImageURL)
        }
      }
    }
  })
}

// Center and crop a video thumbnail into a 600x600 jpg square; 
// Return the image
export const returnSquareVideoThumbnail = async (file) => {
  // console.log('file ->', file)

  return new Promise((resolve, reject) => {
    // Give the selected image a blob URL
    const urlString = URL.createObjectURL(file)
    // console.log('urlString ->', urlString)

    const videoElement = document.createElement('video')
    videoElement.src = urlString
    videoElement.load()
    videoElement.addEventListener('error', (ex) => {
      reject('error when loading video file', ex)
    })

    videoElement.onloadedmetadata = () => {
      // console.log('Video loaded!')
      // console.log('width: ' + videoElement.videoWidth + '\n' + 'height: ' + videoElement.videoHeight + '\n' + 'duration: ' + videoElement.duration)
      // console.log('typeof duration ->', typeof videoElement.duration)

      if (videoElement.duration < videoThumbnailTime) {
        console.log('video is too short')
        resolve('video is too short.')
        return
      }

      // delay seeking or else 'seeked' event won't fire on Safari
      setTimeout(() => {
        videoElement.currentTime = videoThumbnailTime
      }, 200)

      // extract video thumbnail once seeking is complete
      videoElement.addEventListener('seeked', () => {
        // console.log('video is now paused at %ss.', videoThumbnailTime)
        // define a canvas to have the same dimension as the video

        const originalVideoWidth = videoElement.videoWidth
        const originalVideoHeight = videoElement.videoHeight

        const canvasResize = document.createElement('canvas')

        const videoResizeDimension = videoThumbnailDimension
        const aspectRatio = originalVideoWidth / originalVideoHeight

        if (aspectRatio < 1) {
          canvasResize.width = videoResizeDimension
          canvasResize.height = ~~(originalVideoHeight * (videoResizeDimension / originalVideoWidth))
        } else if (aspectRatio > 1) {
          canvasResize.width = ~~(originalVideoWidth * (videoResizeDimension / originalVideoHeight))
          canvasResize.height = videoResizeDimension
        } else {
          canvasResize.width = videoResizeDimension
          canvasResize.height = videoResizeDimension
        }
        const context = canvasResize.getContext('2d', { alpha: false })
        context.drawImage(videoElement, 0, 0, canvasResize.width, canvasResize.height)

        // Convert the square image to a data url
        const resizedImageURL = canvasResize.toDataURL('image/jpg', 1)

        // Convert the data url to an image that can be cropped
        const resizedImageObjectURL = dataURItoBlob(resizedImageURL)
        const urlCropString = URL.createObjectURL(resizedImageObjectURL)
        const imgCrop = new Image()
        imgCrop.src = urlCropString


        // Crop the image onload
        imgCrop.onload = async function() {
    
          // crop image
          if (aspectRatio !== 1) {
            const canvasCrop = document.createElement('canvas')
            canvasCrop.height = videoResizeDimension
            canvasCrop.width = videoResizeDimension
            const contextCrop = canvasCrop.getContext('2d', { alpha: false })
      
            let startX
            let startY
            if (aspectRatio < 1) {
              startX = 0
              startY = (imgCrop.height - videoResizeDimension) / 2
            } else if (aspectRatio > 1) {
              startX = (imgCrop.width - videoResizeDimension) / 2
              startY = 0
            }
        
            contextCrop.drawImage(imgCrop, startX, startY, videoResizeDimension, videoResizeDimension, 0, 0, videoResizeDimension, videoResizeDimension)
      
            // Convert the square image to a data url
            const squareCropImageURL = canvasCrop.toDataURL('image/jpg', 1)

            // console.log('squareCropImageURL ->', squareCropImageURL)
            resolve({
              squareImage: squareCropImageURL,
              duration: Math.round(videoElement.duration),
            })
          } else {

            // console.log('resizedImageURL ->', resizedImageURL)
            resolve({
              squareImage: resizedImageURL,
              duration: Math.round(videoElement.duration),
            })
          }
        }
        
      })
    }

  })
}

// Duration from live URL video
export const returnDurationFromLiveURL = async (videoURLString, setVideoDuration) => {
  return new Promise((resolve, reject) => {
    // Give the selected image a blob URL
    // const urlString = URL.createObjectURL(videoURLString)
    // console.log('urlString ->', urlString)

    const videoElement = document.createElement('video')
    // videoElement.src = `${videoURLString}?${Math.round(Math.random() * 1000000)}`
    videoElement.src = `${videoURLString}?cache-1`
    videoElement.crossOrigin = 'anonymous'
    videoElement.load()
    videoElement.addEventListener('error', (ex) => {
      reject('error when loading video file', ex)
    })

    videoElement.onloadedmetadata = () => {
      // console.log('Video loaded!')
      // console.log('width: ' + videoElement.videoWidth + '\n' + 'height: ' + videoElement.videoHeight + '\n' + 'duration: ' + videoElement.duration)
      // console.log('typeof duration ->', typeof videoElement.duration)

      setVideoDuration(Math.round(videoElement.duration))
      resolve('video duration has set')
    }
  })
}

// Center and crop a video thumbnail into a 600x600 jpg square; 
// Return the image
export const returnVideoThumbnailFromLiveURL = async (videoURLString, setVideoThumbnail, setVideoDuration) => {
  // console.log('videoURLString ->', videoURLString)

  return new Promise((resolve, reject) => {
    // Give the selected image a blob URL
    // const urlString = URL.createObjectURL(videoURLString)
    // console.log('urlString ->', urlString)

    const videoElement = document.createElement('video')
    // videoElement.src = `${videoURLString}?${Math.round(Math.random() * 1000000)}`
    videoElement.src = `${videoURLString}?cache-1`
    videoElement.crossOrigin = 'anonymous'
    videoElement.load()
    videoElement.addEventListener('error', (ex) => {
      reject('error when loading video file', ex)
    })

    videoElement.onloadedmetadata = () => {
      // console.log('Video loaded!')
      // console.log('width: ' + videoElement.videoWidth + '\n' + 'height: ' + videoElement.videoHeight + '\n' + 'duration: ' + videoElement.duration)
      // console.log('typeof duration ->', typeof videoElement.duration)

      setVideoDuration(Math.round(videoElement.duration))

      // delay seeking or else 'seeked' event won't fire on Safari
      setTimeout(() => {
        videoElement.currentTime = videoThumbnailTime
      }, 200)

      // extract video thumbnail once seeking is complete
      videoElement.addEventListener('seeked', () => {
        // console.log('video is now paused at %ss.', videoThumbnailTime)
        // define a canvas to have the same dimension as the video

        const originalVideoWidth = videoElement.videoWidth
        const originalVideoHeight = videoElement.videoHeight

        const canvasResize = document.createElement('canvas')

        const videoResizeDimension = uploadImageDimension
        const aspectRatio = originalVideoWidth / originalVideoHeight

        if (aspectRatio < 1) {
          canvasResize.width = videoResizeDimension
          canvasResize.height = ~~(originalVideoHeight * (videoResizeDimension / originalVideoWidth))
        } else if (aspectRatio > 1) {
          canvasResize.width = ~~(originalVideoWidth * (videoResizeDimension / originalVideoHeight))
          canvasResize.height = videoResizeDimension
        } else {
          canvasResize.width = videoResizeDimension
          canvasResize.height = videoResizeDimension
        }
        const context = canvasResize.getContext('2d', { alpha: false })
        context.drawImage(videoElement, 0, 0, canvasResize.width, canvasResize.height)
        // console.log('context image drawn')

        // Convert the square image to a data url
        const resizedImageURL = canvasResize.toDataURL('image/jpg', 1)
        // console.log('resized Image Url created')

        // Convert the data url to an image that can be cropped
        const resizedImageObjectURL = dataURItoBlob(resizedImageURL)
        const urlCropString = URL.createObjectURL(resizedImageObjectURL)
        const imgCrop = new Image()
        // imgCrop.setAttribute('crossorigin', 'anonymous')
        imgCrop.src = urlCropString
        // console.log('src added to imgCrop')

        // Crop the image onload
        imgCrop.onload = async function() {
    
          // crop image
          if (aspectRatio !== 1) {
            const canvasCrop = document.createElement('canvas')
            canvasCrop.height = videoResizeDimension
            canvasCrop.width = videoResizeDimension
            const contextCrop = canvasCrop.getContext('2d', { alpha: false })
      
            let startX
            let startY
            if (aspectRatio < 1) {
              startX = 0
              startY = (imgCrop.height - videoResizeDimension) / 2
            } else if (aspectRatio > 1) {
              startX = (imgCrop.width - videoResizeDimension) / 2
              startY = 0
            }
        
            contextCrop.drawImage(imgCrop, startX, startY, videoResizeDimension, videoResizeDimension, 0, 0, videoResizeDimension, videoResizeDimension)
      
            // Convert the square image to a data url
            const squareCropImageURL = canvasCrop.toDataURL('image/jpg', 1)

            // console.log('squareCropImageURL ->', squareCropImageURL)
            setVideoThumbnail(squareCropImageURL)
            resolve('video thumbnail has set')
            return
          } else {

            // console.log('resizedImageURL ->', resizedImageURL)
            setVideoThumbnail(resizedImageURL)
            resolve('video thumbnail has set')
            return
          }
        }
        
      })
    }

  })
}


// Create AWS S3 url and then upload image to this url; Used for reviewer profile pictures
export const uploadAWSFileReactS3 = async (dataURL, bucketType, fileName) => {

  // Data URL to blob to file
  const blob = await dataURItoBlob(dataURL)
  const file = await blobImageToFile(blob, fileName)
  // console.log('file ->', file)

  try {
    const formData = new FormData()
    formData.append('imageFile', file)
    formData.append('fileName', fileName)
    formData.append('bucketType', bucketType)
    // console.log('formData ->', formData)

    // Upload data to server, retrieve a reserved image URL from the AWS S3 bucket
    // const { data } = await axios.put(`/api/awsImageURL`, requestObject, {
    const { data } = await axios.put(`${process.env.REACT_APP_SERVER_URL}/api/awsImageURL`, formData, {
      headers: {
        Authorization: `Bearer ${getTokenFromLocalStorage()}`,
        'Content-Type': 'multipart/form-data',
      },
    })
    // console.log('data url ->', data.url)

    return data.url

  } catch (error) {
    // console.log(error)
    return 'No URL' //Don't change this name because it is used elsewhere in the code
  }
}

// Upload multiple images/videos
// Not used anymore because it doesn't handle cropping
export const uploadAWSFilesArray = async (filesArray, bucketType, fileName, userId, email, fromOnboarding = false) => {

  let addedMediaItems = []

  const filteredImageFilesArray = filesArray.filter(fileData => fileData.imageOrVideo === 'image' && fileData.file.size <= maximumCroppedMediaSize)
  const filteredVideoFilesArray = filesArray.filter(fileData => fileData.imageOrVideo === 'video' && fileData.file.size <= maximumCroppedMediaSize && fileData.duration <= maximumVideoDuration)

  // Images — Create URLs and Upload Media Items
  if (filteredImageFilesArray.length > 0) {
    const imagesFormData = new FormData()
    for (let i = 0; i < filteredImageFilesArray.length; i++) {
      imagesFormData.append('mediaFiles', filteredImageFilesArray[i].file)
    }
    imagesFormData.append('mediaType', 'image')
    imagesFormData.append('fileName', fileName)
    imagesFormData.append('bucketType', bucketType)
    imagesFormData.append('userId', userId)
    imagesFormData.append('email', email)

    // Upload data to server
    try {
      if (fromOnboarding && bottomSteps.length > 2){
        // Don't await image uploads in onboarding
        axios.put(`${process.env.REACT_APP_SERVER_URL}/api/awsMediaArrayURLs`, imagesFormData, {
          headers: {
            Authorization: `Bearer ${getTokenFromLocalStorage()}`,
            'Content-Type': 'multipart/form-data',
          },
        })
      } else {
        const { data } = await axios.put(`${process.env.REACT_APP_SERVER_URL}/api/awsMediaArrayURLs`, imagesFormData, {
          headers: {
            Authorization: `Bearer ${getTokenFromLocalStorage()}`,
            'Content-Type': 'multipart/form-data',
          },
        })
        console.log('data ->', data)
        addedMediaItems = [ ...addedMediaItems, ...data]
      }

    } catch (error) {
      console.log(error)
    }
  }

  // Videos — Create URLs and Upload Media Items
  if (filteredVideoFilesArray.length > 0) {
    // Upload videos
    console.log('upload videos')
    const videosFormData = new FormData()
    for (let i = 0; i < filteredVideoFilesArray.length; i++) {
      videosFormData.append('mediaFiles', filteredVideoFilesArray[i].file)
    }
    videosFormData.append('mediaType', 'video')
    videosFormData.append('fileName', fileName)
    videosFormData.append('bucketType', bucketType)
    videosFormData.append('userId', userId)
    videosFormData.append('email', email)

    // Upload data to server
    try {
      if (fromOnboarding && bottomSteps.length > 2){
        // Don't await video uploads in Onboarding
        axios.put(`${process.env.REACT_APP_SERVER_URL}/api/awsMediaArrayURLs`, videosFormData, {
          headers: {
            Authorization: `Bearer ${getTokenFromLocalStorage()}`,
            'Content-Type': 'multipart/form-data',
          },
        })
      } else {
        const { data } = await axios.put(`${process.env.REACT_APP_SERVER_URL}/api/awsMediaArrayURLs`, videosFormData, {
          headers: {
            Authorization: `Bearer ${getTokenFromLocalStorage()}`,
            'Content-Type': 'multipart/form-data',
          },
        })
        console.log('data ->', data)
        addedMediaItems = [ ...addedMediaItems, ...data]
      }

    } catch (error) {
      console.log(error)
    }
  }

  // console.log('Media Upload Finished')
  return addedMediaItems

}

// Upload multiple images/videos with multiple cropped aspect ratios
export const uploadCroppedAWSFilesArray = async (filesArray, bucketType, fileName, userId, email, fromOnboarding = false) => {

  let addedMediaItems = []

  const filteredImageFilesArray = filesArray.filter(fileData => fileData.imageOrVideo === 'image' && 'squareCropFile' in fileData && fileData.squareCropFile !== null && 'size' in fileData.squareCropFile && fileData.squareCropFile.size > 0 && fileData.squareCropFile.size <= maximumCroppedMediaSize && 'portraitCropFile' in fileData && fileData.portraitCropFile !== null && 'size' in fileData.portraitCropFile && fileData.portraitCropFile.size > 0)
  const filteredVideoFilesArray = filesArray.filter(fileData => fileData.imageOrVideo === 'video' && fileData.duration <= maximumVideoDuration && 'squareCropFile' in fileData && fileData.squareCropFile !== null && 'size' in fileData.squareCropFile && fileData.squareCropFile.size > 0 && fileData.squareCropFile.size <= maximumCroppedMediaSize && 'portraitCropFile' in fileData && fileData.portraitCropFile !== null && 'size' in fileData.portraitCropFile && fileData.portraitCropFile.size > 0 && 'videoThumbnailFile' in fileData && fileData.videoThumbnailFile !== null && 'size' in fileData.videoThumbnailFile && fileData.videoThumbnailFile.size > 0)
  console.log('filteredImageFilesArray ->', filteredImageFilesArray.length)
  console.log('filteredVideoFilesArray ->', filteredVideoFilesArray.length)

  // Images — Create URLs and Upload Media Items
  if (filteredImageFilesArray.length > 0) {
    const imagesFormData = new FormData()
    for (let i = 0; i < filteredImageFilesArray.length; i++) {
      imagesFormData.append('mediaFiles', filteredImageFilesArray[i].squareCropFile)
      imagesFormData.append('mediaFiles', filteredImageFilesArray[i].portraitCropFile)
      imagesFormData.append('durationsArray', -1)
      imagesFormData.append('durationsArray', -1)
    }
    imagesFormData.append('mediaType', 'image')
    imagesFormData.append('fileName', fileName)
    imagesFormData.append('bucketType', bucketType)
    imagesFormData.append('userId', userId)
    imagesFormData.append('email', email)
    // console.log('imagesFormData ->', imagesFormData)

    // ? Upload data to server
    try {
      if (fromOnboarding && bottomSteps.length > 2){
        // Don't await image uploads in onboarding
        axios.put(`${process.env.REACT_APP_SERVER_URL}/api/awsMediaArrayCroppedURLs`, imagesFormData, {
          headers: {
            Authorization: `Bearer ${getTokenFromLocalStorage()}`,
            'Content-Type': 'multipart/form-data',
          },
        })
      } else {
        const { data } = await axios.put(`${process.env.REACT_APP_SERVER_URL}/api/awsMediaArrayCroppedURLs`, imagesFormData, {
          headers: {
            Authorization: `Bearer ${getTokenFromLocalStorage()}`,
            'Content-Type': 'multipart/form-data',
          },
        })
        // console.log('image response data ->', data)
        addedMediaItems = [ ...addedMediaItems, ...data]
      }

    } catch (error) {
      console.log(error)
    }
  }


  // Videos — Create URLs and Upload Media Items
  if (filteredVideoFilesArray.length > 0) {
    const videosFormData = new FormData()
    for (let i = 0; i < filteredVideoFilesArray.length; i++) {
      console.log(`file ${i} duration ->`, Math.round(filteredVideoFilesArray[i].duration))
      videosFormData.append('mediaFiles', filteredVideoFilesArray[i].squareCropFile)
      videosFormData.append('mediaFiles', filteredVideoFilesArray[i].portraitCropFile)
      videosFormData.append('mediaFiles', filteredVideoFilesArray[i].videoThumbnailFile)
      videosFormData.append('durationsArray', Math.round(filteredVideoFilesArray[i].duration))
      videosFormData.append('durationsArray', Math.round(filteredVideoFilesArray[i].duration))
      videosFormData.append('durationsArray', Math.round(filteredVideoFilesArray[i].duration))
    }
    videosFormData.append('mediaType', 'video')
    videosFormData.append('fileName', fileName)
    videosFormData.append('bucketType', bucketType)
    videosFormData.append('userId', userId)
    videosFormData.append('email', email)
    // console.log('videosFormData ->', videosFormData)

    // ? Upload data to server
    try {
      if (fromOnboarding && bottomSteps.length > 2){
        // Don't await image uploads in onboarding
        axios.put(`${process.env.REACT_APP_SERVER_URL}/api/awsMediaArrayCroppedURLs`, videosFormData, {
          headers: {
            Authorization: `Bearer ${getTokenFromLocalStorage()}`,
            'Content-Type': 'multipart/form-data',
          },
        })
      } else {
        const { data } = await axios.put(`${process.env.REACT_APP_SERVER_URL}/api/awsMediaArrayCroppedURLs`, videosFormData, {
          headers: {
            Authorization: `Bearer ${getTokenFromLocalStorage()}`,
            'Content-Type': 'multipart/form-data',
          },
        })
        // console.log('video response data ->', data)
        addedMediaItems = [ ...addedMediaItems, ...data]
      }

    } catch (error) {
      console.log(error)
    }
  }

  console.log('final addedMediaItems ->', addedMediaItems)
  console.log('Cropped Media Upload Finished')
  return addedMediaItems
}

// Update image at AWS S3 url
// Not used anymore because it doesn't handle cropping
export const updateAWSFileAtURL = async (updatedMediaItemObj) => {

  if (
    updatedMediaItemObj.file.size <= maximumCroppedMediaSize
  ) {
    try {
      const formData = new FormData()
      formData.append('mediaFile', updatedMediaItemObj.file)
      formData.append('fileName', updatedMediaItemObj.fileName)
      formData.append('urlString', updatedMediaItemObj.urlString)
      formData.append('bucketType', updatedMediaItemObj.bucketType)
      formData.append('mediaType', updatedMediaItemObj.mediaType)
      formData.append('userId', updatedMediaItemObj.userId)
      formData.append('email', updatedMediaItemObj.email)
      formData.append('mediaItemId', updatedMediaItemObj.mediaItemId)
      // console.log('formData ->', formData)

      // Upload data to server, retrieve a reserved image URL from the AWS S3 bucket
      // const { data } = await axios.put(`/api/awsImageURL`, requestObject, {
      const { data } = await axios.put(`${process.env.REACT_APP_SERVER_URL}/api/aws-update-image`, formData, {
        headers: {
          Authorization: `Bearer ${getTokenFromLocalStorage()}`,
          'Content-Type': 'multipart/form-data',
        },
      })
      // console.log('data ->', data)

      return data

    } catch (error) {
      // console.log(error)
      return { updateSuccessful: false } //Don't change this name because it is used elsewhere in the code
    }
  } else {
    return { updateSuccessful: false } //Don't change this name because it is used elsewhere in the code
  }
}

// Update Cropped Image at AWS S3 url
export const updateCroppedAWSFileAtURL = async (updatedMediaItemObj) => {

  if (
    'duration' in updatedMediaItemObj
    && Math.round(updatedMediaItemObj.duration) <= maximumVideoDuration
    && 'squareCropFile' in updatedMediaItemObj 
    && updatedMediaItemObj.squareCropFile !== null 
    && 'size' in updatedMediaItemObj.squareCropFile 
    && updatedMediaItemObj.squareCropFile.size > 0 
    && updatedMediaItemObj.squareCropFile.size <= maximumCroppedMediaSize
    // && updatedMediaItemObj.portraitCropFile.size <= maximumCroppedMediaSize
  ) {
    try {
      const formData = new FormData()
      formData.append('mediaFiles', updatedMediaItemObj.squareCropFile)
      formData.append('mediaFiles', updatedMediaItemObj.portraitCropFile)
      if (updatedMediaItemObj.mediaType === 'video') {
        formData.append('mediaFiles', updatedMediaItemObj.videoThumbnailFile)
      } 
      formData.append('duration', updatedMediaItemObj.duration)
      formData.append('fileName', updatedMediaItemObj.fileName)
      formData.append('squareURLString', updatedMediaItemObj.squareURLString)
      formData.append('portraitURLString', updatedMediaItemObj.portraitURLString)
      formData.append('videoThumbnailURLString', updatedMediaItemObj.videoThumbnailURLString)
      formData.append('bucketType', updatedMediaItemObj.bucketType)
      formData.append('mediaType', updatedMediaItemObj.mediaType)
      formData.append('userId', updatedMediaItemObj.userId)
      formData.append('email', updatedMediaItemObj.email)
      formData.append('mediaItemId', updatedMediaItemObj.mediaItemId)
      // console.log('formData ->', formData)

      // Upload data to server, retrieve a reserved image URL from the AWS S3 bucket
      const { data } = await axios.put(`${process.env.REACT_APP_SERVER_URL}/api/aws-update-cropped-image`, formData, {
        headers: {
          Authorization: `Bearer ${getTokenFromLocalStorage()}`,
          'Content-Type': 'multipart/form-data',
        },
      })
      // console.log('data ->', data)

      return data

    } catch (error) {
      // console.log(error)
      return { updateSuccessful: false } //Don't change this name because it is used elsewhere in the code
    }
  } else {
    console.log('missing data')
    return { updateSuccessful: false } //Don't change this name because it is used elsewhere in the code
  }
}